<script>
    export default {
        name: "TextWithImage",
        props: {
            content: {
                type: String,
                default: ""
            },
            image: {
                type: [Object, String],
                default: null
            },
            video: {
                type: [Object, String],
                default: null
            },
            position: {
                type: String,
                default: ""
            },
            alignImageLeft: Boolean,
            smallImg: Boolean,
            linksAsRegular: Boolean
        },
        data() {
            return {
                imageFormat: {
                    transformation: [{width: 1200, crop: "limit"}]
                }
            };
        }
    };
</script>

<template>
    <div class="textWithImageComp" :class="[position, {alignImageLeft, smallImg}]">
        <div class="@container">
            <div class="componentGrid">
                <div class="contentSection" :class="{contentWithButton: !linksAsRegular}">
                    <slot name="beforeContent"/>
                    <div class="content" v-html="content"/>
                </div>
                <div class="imgSection">
                    <div class="imgContainer">
                        <div class="imgBox" :style="{backgroundImage: $cld.bgUrl(image, imageFormat)}">
                            <video
                                v-if="video"
                                autoplay
                                muted
                                loop
                                playsinline
                                webkit-playsinline
                            >
                                <source :src="$cld.url(video, {resource_type: 'video'})">
                            </video>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .textWithImageComp {
        overflow-x: hidden;
        --gap: 50px;

        .contentWithButton :deep(a:not(.regLink)) {
            margin-top: 15px;
            @screen desktop {
                margin-top: 22px;
            }
        }

        .componentGrid {
            display: grid;
            grid-template-columns: auto;
            grid-template-rows: auto;
            align-items: center;
            grid-template-areas: "imgSection" "contentSection";
            @screen tablet {
                grid-template-columns: 50% 50%;
                grid-template-rows: 100%;
                grid-template-areas: "contentSection imgSection";
            }
        }

        .contentSection {
            grid-area: contentSection;
            margin-right: 0;
            margin-top: 25px;
            @screen tablet {
                margin-right: var(--gap);
                margin-top: 0;
            }

            :deep(h1) {
                margin-bottom: 24px;
            }

            :deep(p) {
                color: var(--sfc-dark-grey)
            }


            .content {
                :deep(p:last-child) {
                    margin-bottom: 0;
                }
            }
        }

        .imgSection {
            grid-area: imgSection;

            .imgContainer {
                width: 100%;
                padding-bottom: 100%;
                position: relative;

                @screen desktop {
                    min-height: 620px;
                }

                .imgBox {
                    position: absolute;
                    width: 100vw;
                    height: 100%;
                    left: 0;
                    top: 0;
                    background-size: cover;
                    background-position: right center;
                    background-repeat: no-repeat;
                    border-radius: 400px/400px;
                    border-bottom-right-radius: 0;
                    border-top-right-radius: 0;
                    overflow: hidden;
                    transform: translate3d(0, 0, 0);

                    @screen tablet {
                        width: calc(50vw);
                    }

                    video {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        left: 0;
                        top: 0;
                        object-fit: cover;
                    }
                }
            }
        }

        &:deep(.smallImg) {
            .imgSection {
                .imgContainer {
                    min-height: 495px;
                    padding-bottom: 68%;
                    .imgBox {
                        min-height: 495px;
                        height: auto;
                    }
                    @screen tablet {
                        padding-bottom: 0;
                    }
                    @screen mobile {
                        .imgBox {
                            width: 100%;
                        }
                    }
                }
            }

            &:not(.alignImageBottom), &:not(.alignImageTop) {
                left: 50px;
            }

            &.alignImageLeft {
                .componentGrid {
                    .imgSection {
                        .imgBox {
                            left: auto;
                            right: 50px;
                        }
                    }
                }
            }
        }

        &.alignImageLeft, &.alignImageLeftTop, &.alignImageLeftBottom {
            .componentGrid {
                @screen tablet {
                    grid-template-areas: "imgSection contentSection";
                }

                .contentSection {
                    margin-left: 0;
                    margin-right: 0;
                    @screen tablet {
                        margin-left: var(--gap);
                    }
                }

                .imgSection {
                    .imgBox {
                        left: auto;
                        right: 0;
                        border-radius: 400px/400px;
                        border-bottom-left-radius: 0;
                        border-top-left-radius: 0;
                    }
                }
            }
        }

        &.alignImageTop, &.alignImageLeftTop, &.alignImageBottom, &.alignImageLeftBottom {
            min-height: 0;

            .componentGrid {
                .imgSection {
                    .imgContainer {
                        .imgBox {
                            border-radius: 400px/400px;
                            border-top-right-radius: 0;
                            border-top-left-radius: 0;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            min-height: 495px;
                            height: auto;
                            max-width: 495px;
                            background-position: center bottom;
                        }

                        @screen mobile {
                            min-height: 375px;
                            .imgBox {
                                min-height: 375px;
                                max-width: 375px;
                            }
                        }
                    }
                }
            }
        }

        &.alignImageBottom, &.alignImageLeftBottom {
            .componentGrid {
                .imgSection {
                    .imgContainer {
                        .imgBox {
                            border-radius: 400px/400px;
                            border-bottom-right-radius: 0;
                            border-bottom-left-radius: 0;
                            background-position: center top;
                        }
                    }
                }
            }
        }

        &.s60-40 {
            @screen mobile {
                text-align: center;
                .contentSection {
                    margin-top: 48px;

                    .conentHeader:after {
                        left: 50%;
                        transform: translateX(-50%);
                    }
                }
            }

            @screen desktop {
                .componentGrid {
                    grid-template-columns: 40% 60%;

                    .imgSection .imgContainer {
                        min-height: 550px;
                        padding-bottom: 75%;

                        .imgBox {
                            width: calc(60vw);
                        }
                    }
                }

                .contentSection {
                    margin-left: 0;
                    margin-right: 100px;
                }

                &.alignImageLeft {
                    .contentSection {
                        margin-left: 100px;
                        margin-right: 0;
                    }

                    .componentGrid {
                        grid-template-columns: 60% 40%;
                    }
                }
            }
        }
    }
</style>
